/**
 * OS notifications for cooking-mode timers. The in-page alarm (beep, buzz,
 * flashing countdown) always fires; a system notification is added only when
 * the page is hidden — another tab, the phone locked, the app in the
 * background — and the user has allowed notifications.
 */

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

/** True when a finished timer should also raise an OS notification. */
export function shouldNotify({
  visibility,
  permission,
}: {
  visibility: DocumentVisibilityState | undefined;
  permission: NotificationPermissionState;
}): boolean {
  return permission === 'granted' && visibility !== undefined && visibility !== 'visible';
}

/**
 * True when starting a timer should ask for notification permission: only
 * while the browser still says "default", and only once per session (a
 * dismissed or refused prompt is remembered so we never nag).
 */
export function shouldAskPermission({
  permission,
  askedThisSession,
}: {
  permission: NotificationPermissionState;
  askedThisSession: boolean;
}): boolean {
  return permission === 'default' && !askedThisSession;
}

/** "Roast chicken · Baste the bird" — the recipe first so a glance says which pan. */
export function timerNotificationTitle(recipeTitle: string, stepText: string): string {
  const recipe = recipeTitle.trim();
  const step = stepText.trim();
  if (!recipe) return step || 'Timer done';
  if (!step) return recipe;
  return `${recipe} · ${step}`;
}

// ------------------------------------------------------------- browser glue --

const ASKED_KEY = 'fewerbags:cook:notify-asked';

export function currentPermission(): NotificationPermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

function askedThisSession(): boolean {
  try {
    return sessionStorage.getItem(ASKED_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberAsked(): void {
  try {
    sessionStorage.setItem(ASKED_KEY, '1');
  } catch {
    // Private mode — we may ask again after a reload; harmless.
  }
}

/** Call from the timer-start click so the browser treats it as a user gesture. */
export function maybeRequestNotificationPermission(): void {
  const permission = currentPermission();
  if (!shouldAskPermission({ permission, askedThisSession: askedThisSession() })) return;
  rememberAsked();
  try {
    void Notification.requestPermission().catch(() => undefined);
  } catch {
    // Old Safari: callback-only API or none at all — the in-page alarm still works.
  }
}

/**
 * Raise an OS notification for a finished timer if the page is hidden and
 * notifications are allowed. Goes through the service worker when there is
 * one (the only way that works on Android Chrome and for installed apps), and
 * falls back to a page-level Notification otherwise.
 */
export function notifyTimerDone({
  recipeTitle,
  stepText,
  planId,
  stepId,
}: {
  recipeTitle: string;
  stepText: string;
  planId: string;
  stepId: string;
}): void {
  const visibility = typeof document === 'undefined' ? undefined : document.visibilityState;
  if (!shouldNotify({ visibility, permission: currentPermission() })) return;

  const title = timerNotificationTitle(recipeTitle, stepText);
  const url = `/plan/${planId}/cook`;
  const options: NotificationOptions = {
    body: 'Timer done',
    tag: `fewerbags-timer-${planId}-${stepId}`,
    icon: '/icons/icon-192.png',
    data: { url, planId },
  };

  const pageNotification = () => {
    try {
      const n = new Notification(title, options);
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      // Android Chrome forbids the constructor; nothing else to try.
    }
  };

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready
      .then((registration) => registration.showNotification(title, options))
      .catch(pageNotification);
  } else {
    pageNotification();
  }
}
